import styled, { createGlobalStyle, keyframes } from 'styled-components'

// ─── ANIMATIONS ───────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

// ─── THEME COLORS (shared palette) ────────────────────────────────────────────
export const c = {
  bg:     '#0a0c0f',
  bg2:    '#111318',
  bg3:    '#181c23',
  bg4:    '#1e2330',
  bdr:    '#1f2430',
  bdr2:   '#2a3040',
  acc:    '#00e5a0',
  acc2:   '#00b87a',
  adim:   'rgba(0,229,160,.08)',
  adim2:  'rgba(0,229,160,.15)',
  txt:    '#e8ecf0',
  txt2:   '#8a95a3',
  txt3:   '#5a6370',
  warn:   '#f59e0b',
  danger: '#ef4444',
  info:   '#3b82f6',
  mono:   "'Space Mono', monospace",
  sans:   "'DM Sans', sans-serif",
}

// ─── GLOBAL STYLES ─────────────────────────────────────────────────────────────
export const GlobalHomeStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: ${c.bg2}; }
  ::-webkit-scrollbar-thumb { background: ${c.bdr2}; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: ${c.txt3}; }
`

// ─── PAGE WRAPPER ─────────────────────────────────────────────────────────────
export const PageWrap = styled.div`
  background: ${c.bg};
  color: ${c.txt};
  font-family: ${c.sans};
  min-height: 100vh;
  overflow-x: hidden;
`

// ─── HEADER ───────────────────────────────────────────────────────────────────
export const Header = styled.header`
  background: ${c.bg2};
  border-bottom: 1px solid ${c.bdr};
  padding: 0 32px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  gap: 10px;

  @media (max-width: 900px) { padding: 0 14px; }
  @media (max-width: 600px) {
    padding: 0 10px;
    height: auto;
    min-height: 52px;
    flex-wrap: wrap;
    gap: 6px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
`

export const Logo = styled.div`
  font-family: ${c.mono};
  font-size: 18px;
  font-weight: 700;
  color: ${c.danger};
  letter-spacing: -0.5px;
  span { color: ${c.txt}; }
  @media (max-width: 600px) { font-size: 16px; }
`

export const HeaderStatus = styled.div`
  font-size: 12px;
  color: ${c.txt3};
  font-family: ${c.mono};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  @media (max-width: 600px) {
    font-size: 10px;
    width: 100%;
    justify-content: flex-start;
    > span { white-space: normal; }
  }
`

export const StatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $active }) => $active ? c.acc : c.txt3};
  box-shadow: ${({ $active }) => $active ? `0 0 8px ${c.acc}` : 'none'};
  animation: ${({ $active }) => $active ? pulse : 'none'} 2s infinite;
`

// ─── NAVIGATION ───────────────────────────────────────────────────────────────
export const NavBar = styled.nav`
  background: ${c.bg2};
  border-bottom: 1px solid ${c.bdr};
  padding: 0 32px;
  display: flex;
  overflow-x: auto;
  scrollbar-width: thin;
  &::-webkit-scrollbar { height: 3px; }
  &::-webkit-scrollbar-thumb { background: ${c.bdr2}; }

  @media (max-width: 900px) { padding: 0 12px; }
  @media (max-width: 600px) { padding: 0 8px; }
`

export const NavTab = styled.button`
  padding: 12px 20px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $active }) => $active ? c.acc : c.txt3};
  cursor: pointer;
  border: none;
  background: transparent;
  border-bottom: 2px solid ${({ $active }) => $active ? c.acc : 'transparent'};
  transition: all 0.2s;
  white-space: nowrap;
  user-select: none;
  font-family: ${c.sans};

  &:hover { color: ${({ $active }) => $active ? c.acc : c.txt2}; }
  @media (max-width: 600px) { padding: 10px 12px; font-size: 12px; }
`

// ─── MAIN CONTENT ─────────────────────────────────────────────────────────────
export const MainContent = styled.main`
  padding: 28px 32px;
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.3s ease;

  @media (max-width: 900px) { padding: 16px; }
  @media (max-width: 600px) { padding: 12px 10px; }
`

// ─── UPLOAD TAB ───────────────────────────────────────────────────────────────
export const UploadHero = styled.div`
  text-align: center;
  padding: 80px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;

  h1 {
    font-family: ${c.mono};
    font-size: 42px;
    font-weight: 700;
    color: ${c.txt};
    letter-spacing: -2px;
    line-height: 1;
    margin-bottom: 8px;
  }

  > p {
    color: ${c.txt3};
    font-size: 14px;
    margin-bottom: 48px;
  }

  @media (max-width: 900px) {
    padding: 48px 16px;
    h1 { font-size: 28px; }
  }
`

export const DropZone = styled.div`
  width: 100%;
  max-width: 520px;
  border: 2px dashed ${({ $dragOver }) => $dragOver ? c.acc : c.bdr2};
  border-radius: 16px;
  padding: 48px 32px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${({ $dragOver }) => $dragOver ? c.adim : c.bg2};
  position: relative;
  text-align: center;

  &:hover { border-color: ${c.acc}; background: ${c.adim}; }

  input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }

  .drop-icon { font-size: 40px; margin-bottom: 16px; }
  h3 { font-size: 16px; font-weight: 600; margin-bottom: 6px; color: ${c.txt}; }
  > p { font-size: 13px; color: ${c.txt3}; }
`

export const UploadBtn = styled.button`
  margin-top: 20px;
  background: ${c.acc};
  color: #000;
  border: none;
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  font-family: ${c.sans};
  cursor: pointer;
  transition: background 0.2s;
  display: inline-block;
  position: relative;
  z-index: 1;

  &:hover { background: ${c.acc2}; }
`

export const UploadHint = styled.p`
  font-size: 12px;
  color: ${c.txt3};
  margin-top: 16px;
  code { color: ${c.acc}; }
`

export const ResetBtn = styled.button`
  background: ${c.bg3};
  color: ${c.txt2};
  border: 1px solid ${c.bdr2};
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: ${c.sans};
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 14px;

  &:hover { border-color: ${c.danger}; color: ${c.danger}; }
`

// ─── STATS GRID ───────────────────────────────────────────────────────────────
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 24px;
`

export const StatCard = styled.div`
  background: ${c.bg2};
  border: 1px solid ${c.bdr};
  border-radius: 12px;
  padding: 18px;
`

export const StatLabel = styled.div`
  font-size: 11px;
  color: ${c.txt3};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
`

export const StatValue = styled.div`
  font-size: 28px;
  font-family: ${c.mono};
  font-weight: 700;
  color: ${({ $variant }) =>
    $variant === 'accent' ? c.acc :
    $variant === 'warn'   ? c.warn :
    $variant === 'info'   ? c.info :
    $variant === 'danger' ? c.danger :
    c.txt};
`

export const StatSub = styled.div`
  font-size: 11px;
  color: ${c.txt3};
  margin-top: 4px;
`

// ─── CARD ─────────────────────────────────────────────────────────────────────
export const Card = styled.div`
  background: ${c.bg2};
  border: 1px solid ${c.bdr};
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
`

export const CardHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${c.bdr};
  display: flex;
  align-items: center;
  gap: 10px;

  h3 { font-size: 14px; font-weight: 600; color: ${c.txt}; margin: 0; }
`

export const CardBadge = styled.span`
  font-size: 10px;
  font-family: ${c.mono};
  background: ${c.adim};
  color: ${c.acc};
  padding: 2px 8px;
  border-radius: 4px;
`

export const CardBody = styled.div`padding: 20px;`

// ─── CONSULTA LAYOUT ──────────────────────────────────────────────────────────
export const ConsultaGrid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`

export const ConsultaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

// ─── SCAN / NF INPUT ──────────────────────────────────────────────────────────
export const ScanLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${c.txt3};
  letter-spacing: 0.8px;
  text-transform: uppercase;
  margin-bottom: 8px;
  display: block;
`

export const ScanInputWrap = styled.div`
  position: relative;
  margin-bottom: 16px;
`

export const ScanInput = styled.input`
  width: 100%;
  background: ${c.bg3};
  border: 1.5px solid ${c.bdr2};
  border-radius: 8px;
  padding: 12px 44px 12px 14px;
  color: ${c.txt};
  font-size: 14px;
  font-family: ${c.mono};
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: ${c.acc}; }
  &::placeholder { color: ${c.txt3}; }
`

export const ScanIcon = styled.span`
  position: absolute;
  right: 12px;
  bottom: 12px;
  color: ${c.txt3};
  font-size: 18px;
  pointer-events: none;
`

export const NfChipsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`

export const NfChip = styled.div`
  font-family: ${c.mono};
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${({ $highlight }) => $highlight ? c.adim : c.bg3};
  border: 1px solid ${({ $highlight }) => $highlight ? c.acc : c.bdr2};
  color: ${({ $highlight }) => $highlight ? c.acc : c.txt3};
`

// ─── BIG RESULT PANEL ─────────────────────────────────────────────────────────
export const BigResult = styled.div`
  background: ${c.bg2};
  border: 1px solid ${c.bdr};
  border-radius: 16px;
  overflow: hidden;
  animation: ${fadeInUp} 0.25s ease;
`

export const BigResultHeader = styled.div`
  background: ${c.adim};
  border-bottom: 1px solid ${c.adim2};
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  @media (max-width: 900px) { padding: 16px 18px; }
  @media (max-width: 600px) {
    padding: 14px 16px; flex-wrap: wrap; gap: 10px;
    > div:last-child { text-align: left !important; }
  }
`

export const BigNfLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${c.acc2};
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-bottom: 4px;
`

export const BigNfNumber = styled.div`
  font-family: ${c.mono};
  font-size: 52px;
  font-weight: 700;
  color: ${c.acc};
  line-height: 1;
  letter-spacing: -2px;

  @media (max-width: 900px) { font-size: 36px; }
  @media (max-width: 600px) { font-size: 28px; letter-spacing: -1px; }
`

export const BigSeqVal = styled.div`
  font-family: ${c.mono};
  font-size: 52px;
  font-weight: 700;
  color: ${c.txt};
  line-height: 1;
  letter-spacing: -2px;

  @media (max-width: 900px) { font-size: 36px; }
  @media (max-width: 600px) { font-size: 28px; letter-spacing: -1px; }
`

export const BigFieldsGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`

export const BigField = styled.div`
  padding: 22px 28px;
  border-right: ${({ $noRight }) => $noRight ? 'none' : `1px solid ${c.bdr}`};
  border-bottom: ${({ $noBorderBottom }) => $noBorderBottom ? 'none' : `1px solid ${c.bdr}`};
  grid-column: ${({ $full }) => $full ? '1 / -1' : 'auto'};

  @media (max-width: 900px) { padding: 16px 20px; }
  @media (max-width: 600px) {
    padding: 12px 16px;
    border-right: none;
    grid-column: 1 / -1;
  }
`

export const BigFieldLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${c.txt3};
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 8px;
`

export const BigFieldVal = styled.div`
  font-size: ${({ $sm }) => $sm ? '24px' : '38px'};
  font-weight: 600;
  color: ${({ $variant }) =>
    $variant === 'accent' ? c.acc :
    $variant === 'danger' ? c.danger :
    c.txt};
  line-height: 1.2;
  word-break: break-word;
  font-family: ${({ $mono }) => $mono ? c.mono : 'inherit'};

  @media (max-width: 900px) { font-size: ${({ $sm }) => $sm ? '18px' : '24px'}; }
  @media (max-width: 600px) { font-size: ${({ $sm }) => $sm ? '16px' : '22px'}; }
`

// Linha de métricas no rodapé do painel: agora com 2 colunas (Volumes e Peso).
export const BigMetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-top: 1px solid ${c.bdr};
`

// Célula de métrica reutilizada tanto dentro do BigFieldsGrid (Bipados)
// quanto na BigMetricsRow (Volumes/Peso). Por padrão tem borda à direita
// e borda inferior — use $noRight / $noBorderBottom para suprimi-las.
export const BigMetric = styled.div`
  padding: 18px 28px;
  border-right: ${({ $noRight }) => $noRight ? 'none' : `1px solid ${c.bdr}`};
  border-bottom: ${({ $noBorderBottom }) => $noBorderBottom ? 'none' : `1px solid ${c.bdr}`};
  text-align: center;
  @media (max-width: 900px) { padding: 14px 18px; }
  @media (max-width: 600px) { padding: 12px 14px; }
`

export const BigMetricLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${c.txt3};
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 6px;
`

export const BigMetricVal = styled.div`
  font-family: ${c.mono};
  font-size: 32px;
  font-weight: 700;
  color: ${({ $color }) => $color || c.txt};
  @media (max-width: 900px) { font-size: 26px; }
  @media (max-width: 600px) { font-size: 22px; }
`

export const BipBadge = styled.div`
  margin-top: 6px;
  display: inline-block;
  background: ${c.acc};
  color: #000;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: ${c.mono};
`

// ─── DETAIL SECTION ───────────────────────────────────────────────────────────
export const DetailSection = styled.div`
  background: ${c.bg2};
  border: 1px solid ${c.bdr};
  border-radius: 14px;
  overflow: hidden;
  margin-top: 16px;
`

export const DetailHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid ${c.bdr};
  h3 { font-size: 13px; font-weight: 600; color: ${c.txt3}; margin: 0; }
`

export const DetailRow = styled.div`
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid ${c.bdr};
  gap: 12px;
  align-items: flex-start;

  &:last-child { border-bottom: none; }
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 2px;
    padding: 10px 14px;
  }
`

export const DetailKey = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${c.txt3};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  min-width: 110px;
  padding-top: 2px;
  flex-shrink: 0;
  @media (max-width: 600px) { min-width: 0; font-size: 10px; padding-top: 0; }
`

export const DetailVal = styled.div`
  font-size: 14px;
  color: ${({ $variant }) =>
    $variant === 'accent' ? c.acc :
    $variant === 'warn'   ? c.warn :
    $variant === 'danger' ? c.danger :
    c.txt};
  font-weight: 500;
  flex: 1;
  word-break: break-word;
  font-family: ${({ $mono }) => $mono ? c.mono : 'inherit'};
`

// ─── EMPTY / NOT FOUND STATES ─────────────────────────────────────────────────
export const EmptyPlaceholder = styled.div`
  text-align: center;
  padding: 60px 32px;
  background: ${c.bg2};
  border: 1px solid ${c.bdr};
  border-radius: 16px;
  color: ${c.txt3};

  .icon { font-size: 32px; margin-bottom: 12px; }
  p    { font-size: 13px; }
`

export const NotFoundBox = styled.div`
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  color: ${c.danger};
  font-size: 14px;
`

export const NoDataScreen = styled.div`
  min-height: calc(100vh - 130px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  color: ${c.txt3};

  .icon { font-size: 48px; }
  p    { font-size: 14px; }
`

export const GoUploadBtn = styled.button`
  background: ${c.acc};
  color: #000;
  border: none;
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  font-family: ${c.sans};
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: ${c.acc2}; }
`

// ─── TABLE / ENTREGAS ─────────────────────────────────────────────────────────
export const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
`

export const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  background: ${c.bg2};
  border: 1.5px solid ${c.bdr2};
  border-radius: 8px;
  padding: 9px 14px;
  color: ${c.txt};
  font-size: 13px;
  font-family: ${c.sans};
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: ${c.acc}; }
  &::placeholder { color: ${c.txt3}; }
`

export const FilterSelect = styled.select`
  background: ${c.bg2};
  border: 1.5px solid ${c.bdr2};
  border-radius: 8px;
  padding: 9px 12px;
  color: ${c.txt};
  font-size: 13px;
  font-family: ${c.sans};
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
  min-width: 160px;

  &:focus { border-color: ${c.acc}; }
  option  { background: ${c.bg2}; }
`

export const TableWrap = styled.div`
  background: ${c.bg2};
  border: 1px solid ${c.bdr};
  border-radius: 14px;
  overflow: auto;
  max-height: calc(100vh - 280px);
`

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  thead tr {
    background: ${c.bg3};
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th {
    padding: 10px 14px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: ${c.txt3};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid ${c.bdr};
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    font-family: ${c.sans};

    &:hover { color: ${c.txt2}; }
  }

  td {
    padding: 10px 14px;
    border-bottom: 1px solid ${c.bdr};
    color: ${c.txt2};
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tr:last-child td { border-bottom: none; }
  tbody tr:hover td { background: rgba(255,255,255,.02); color: ${c.txt}; }
`

export const RowCount = styled.div`
  font-size: 12px;
  color: ${c.txt3};
  font-family: ${c.mono};
  padding: 8px 14px;
  border-top: 1px solid ${c.bdr};
`

export const Tag = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: ${c.mono};
  white-space: nowrap;
  background: ${({ $variant }) =>
    $variant === 'aguardando' ? 'rgba(245,158,11,0.12)' :
    $variant === 'aceita'     ? 'rgba(59,130,246,0.12)'  :
    $variant === 'finalizada' ? 'rgba(0,229,160,0.12)'   :
    'rgba(138,149,163,0.12)'};
  color: ${({ $variant }) =>
    $variant === 'aguardando' ? '#f59e0b' :
    $variant === 'aceita'     ? '#3b82f6' :
    $variant === 'finalizada' ? '#00e5a0' :
    '#8a95a3'};
`

// ─── MOTORISTAS ───────────────────────────────────────────────────────────────
export const MotoristasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`

export const MotoristaCard = styled.div`
  background: ${c.bg2};
  border: 1px solid ${c.bdr};
  border-radius: 14px;
  overflow: hidden;
`

export const MotoristaHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${c.bdr};
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 600px) { padding: 14px 16px; }
`

export const MotoristaAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $danger }) => $danger ? 'rgba(239,68,68,0.1)' : 'rgba(0,229,160,.08)'};
  border: 1.5px solid ${({ $danger }) => $danger ? '#ef4444' : '#00e5a0'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $danger }) => $danger ? '#ef4444' : '#00e5a0'};
  font-family: ${c.mono};
  flex-shrink: 0;
`

export const MotoristaName  = styled.div`font-size: 13px; font-weight: 600; color: ${({ $danger }) => $danger ? '#ef4444' : c.txt};`
export const MotoristaPhone = styled.div`font-size: 11px; color: ${c.txt3}; font-family: ${c.mono};`

export const MotoristaStats = styled.div`
  padding: 14px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
  gap: 8px;
  @media (max-width: 600px) {
    padding: 12px 16px;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
`

export const MStat      = styled.div`text-align: center;`
export const MStatVal   = styled.div`
  font-size: 20px;
  font-family: ${c.mono};
  font-weight: 700;
  color: ${({ $variant }) =>
    $variant === 'accent' ? '#00e5a0' :
    $variant === 'warn'   ? '#f59e0b' :
    $variant === 'info'   ? '#3b82f6' :
    $variant === 'danger' ? '#ef4444' :
    c.txt};
`
export const MStatLabel = styled.div`font-size: 10px; color: ${c.txt3}; margin-top: 2px;`

export const MotoristaRotas = styled.div`
  padding: 0 20px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

export const RotaBadge = styled.span`
  font-size: 10px;
  font-family: ${c.mono};
  background: ${c.bg3};
  border: 1px solid ${c.bdr2};
  padding: 3px 8px;
  border-radius: 4px;
  color: ${c.txt3};
`

// ─── TOAST NOTIFICATION ───────────────────────────────────────────────────────
export const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: ${c.bg2};
  border: 1px solid ${c.bdr2};
  border-left: 3px solid ${({ $error }) => $error ? '#ef4444' : '#00e5a0'};
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 13px;
  color: ${c.txt};
  transform: ${({ $visible }) => $visible ? 'translateY(0)' : 'translateY(80px)'};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: all 0.3s;
  @media (max-width: 600px) {
    bottom: 12px; right: 12px; left: 12px;
    max-width: none; font-size: 12px; padding: 12px 14px;
  }
  z-index: 999;
  max-width: 320px;
  pointer-events: none;
`

// ─── BOX SCAN MODAL ────────────────────────────────────────────────────────
export const BoxModalOverlay = styled.div`
  position:fixed; inset:0; z-index:2000;
  background:rgba(0,0,0,.7); backdrop-filter:blur(3px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center;
  padding:20px;
`
export const BoxModalBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:14px;
  padding:22px 26px; width:1320px; max-width:100%;
  max-height:calc(100vh - 40px); overflow-y:auto;
  box-shadow:0 16px 48px rgba(0,0,0,.7);
  border-top:3px solid ${c.acc};
  h3 { font-size:18px; font-weight:600; color:${c.txt}; margin-bottom:4px; }
  > p { font-size:13px; color:${c.txt3}; margin-bottom:14px; line-height:1.5; }
  &::-webkit-scrollbar { width:5px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
  @media (max-width: 900px) {
    padding: 16px 14px;
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
    h3 { font-size:15px; }
    > p { font-size:12px; margin-bottom:10px; }
  }
  @media (max-width: 600px) {
    padding: 12px 14px;
    max-width: calc(100vw - 8px);
    max-height: calc(100vh - 8px);
  }
`
/* Aumenta TODO o conteúdo do modal em 30% — operador precisa enxergar nítido.
   No mobile zoom volta pra 1.0 pra não estourar a viewport. */
export const BoxModalScale = styled.div`
  zoom: 1.3;
  @media (max-width: 900px) { zoom: 1; }
`
export const BoxModalGrid = styled.div`
  display:grid; grid-template-columns: 1.4fr 1fr; gap:18px;
  align-items:start;
  @media (max-width: 920px) { grid-template-columns: 1fr; gap: 12px; }
`
export const BoxModalRight = styled.div`
  display:flex; flex-direction:column; gap:10px;
`
export const VolumeHeader = styled.div`
  display:grid; grid-template-columns: 1fr 1fr 1fr; gap:10px;
  margin-bottom:16px; padding:12px;
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:9px;
  > div {
    .lbl { font-size:9px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.06em; }
    .val { font-family:${c.mono}; font-size:13px; font-weight:700; color:${c.txt}; margin-top:2px;
           white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .sub { font-size:10px; color:${c.txt3}; margin-top:1px; }
  }
`
export const SuggestionCard = styled.div`
  margin-bottom:14px; padding:14px 16px;
  background: ${({ $kind }) =>
    $kind === 'mandatory' ? 'rgba(239,68,68,.12)' :
    $kind === 'reuse'     ? 'rgba(245,158,11,.10)' :
    $kind === 'sequence'  ? 'rgba(0,229,160,.08)'  :
    'rgba(94,99,112,.10)'};
  border: ${({ $kind }) =>
    $kind === 'mandatory' ? `2px solid ${c.danger}` :
    `1px solid ${
      $kind === 'reuse'    ? '#f59e0b' :
      $kind === 'sequence' ? '#00b87a' :
      c.bdr2
    }`};
  border-radius:10px;
  display:flex; align-items:center; gap:14px;
  animation:${fadeInUp} .2s ease;
  .icon { font-size:32px; flex-shrink:0; }
  .info { flex:1; min-width:0; }
  .reason {
    font-size:10px; text-transform:uppercase; letter-spacing:.06em;
    color:${({ $kind }) =>
      $kind === 'mandatory' ? c.danger :
      $kind === 'reuse'     ? '#f59e0b' :
      '#00e5a0'};
    font-weight:700;
  }
  .name {
    font-family:${c.mono}; font-size:18px; font-weight:700; margin-top:3px;
    color:${({ $kind }) =>
      $kind === 'mandatory' ? '#fca5a5' :
      $kind === 'reuse'     ? '#fbbf24' :
      '#00e5a0'};
  }
  .meta { font-size:11px; color:${c.txt2}; margin-top:4px;
          .prev-vols { font-family:${c.mono}; color:${c.txt}; font-weight:600; } }
  .barcode {
    font-family:${c.mono}; font-size:10px;
    background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:5px;
    padding:3px 8px; color:${c.txt3}; margin-top:5px; display:inline-block;
  }
`
export const SuggestionLoading = styled.div`
  margin-bottom:14px; padding:14px;
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:9px;
  text-align:center; font-size:11px; color:${c.txt3};
`
export const SuggestionEmpty = styled.div`
  margin-bottom:14px; padding:14px 16px;
  background:rgba(239,68,68,.08); border:1px solid ${c.danger}; border-radius:9px;
  color:${c.danger}; font-size:12px; font-weight:500;
  display:flex; align-items:center; gap:10px;
  .icon { font-size:20px; }
`
export const BoxScanLabel = styled.div`
  font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.06em;
  margin-bottom:6px; margin-top:6px;
`
export const BoxScanInput = styled.input`
  width:100%; background:${c.bg3};
  border:2px solid ${({ $kind }) =>
    $kind === 'err' ? c.danger : $kind === 'ok' ? c.acc : c.bdr2};
  border-radius:10px; padding:14px 16px;
  color:${c.txt}; font-size:16px; font-family:${c.mono};
  outline:none; text-align:center; letter-spacing:.04em;
  transition:.15s;
  &:focus { border-color:${c.acc}; box-shadow:0 0 0 4px rgba(0,229,160,.18); }
  &:disabled { opacity:.6; }
  &::placeholder { color:${c.txt3}; letter-spacing:.02em; font-size:13px; }
`
export const BoxScanFeedback = styled.div`
  margin-top:10px; min-height:18px;
  font-size:12px; font-weight:500;
  color:${({ $kind }) => $kind === 'ok' ? c.acc : $kind === 'err' ? c.danger : c.txt3};
`
export const MovedAlert = styled.div`
  margin-top:10px; padding:10px 12px;
  background:rgba(245,158,11,.10); border:1px solid #f59e0b; border-radius:7px;
  font-size:11px; color:#fbbf24;
  display:flex; align-items:center; gap:8px;
`
export const BoxModalActions = styled.div`
  display:flex; justify-content:space-between; align-items:center; gap:8px; margin-top:18px;
  .hint { font-size:10px; color:${c.txt3}; flex:1; }
  @media (max-width: 600px) {
    flex-direction: column-reverse;
    .hint { display: none; }
  }
`
export const BoxModalBtn = styled.button`
  padding:9px 18px; border-radius:8px; cursor:pointer;
  font-size:12px; font-weight:600; transition:.15s; font-family:${c.sans};
  background:${({ $primary }) => $primary ? c.acc : 'transparent'};
  border:1px solid ${({ $primary }) => $primary ? c.acc : c.bdr2};
  color:${({ $primary }) => $primary ? '#000' : c.txt2};
  &:hover { ${({ $primary }) => $primary ? 'filter:brightness(1.1);' : `border-color:${c.acc}; color:${c.acc};`} }
  &:disabled { opacity:.5; cursor:not-allowed; }
  @media (max-width: 600px) {
    width: 100%; padding: 13px; font-size: 14px;
  }
`

// ─── ENTREGAS · Accordion ──────────────────────────────────────────────────
export const EntregasToolbar = styled.div`
  display:flex; align-items:center; gap:10px; margin-bottom:14px; flex-wrap:wrap;
`
export const StatusChip = styled.button`
  background:${({ $active }) => $active ? c.adim2 : 'transparent'};
  border:1px solid ${({ $active }) => $active ? c.acc2 : c.bdr2};
  color:${({ $active }) => $active ? c.acc : c.txt2};
  border-radius:14px; padding:6px 14px;
  font-size:11px; font-weight:500; cursor:pointer; transition:.12s;
  display:inline-flex; align-items:center; gap:6px;
  font-family:${c.sans};
  &:hover { border-color:${c.acc}; color:${c.acc}; }
  .count { font-family:${c.mono}; font-size:10px; font-weight:700; opacity:.8; }
`
export const AccordionList = styled.div`
  display:flex; flex-direction:column; gap:8px;
`
export const AccordionItem = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:9px;
  overflow:hidden; transition:.15s;
  &:hover { border-color:${c.bdr2}; }
`
export const AccordionHeader = styled.button`
  display:grid; grid-template-columns: 110px 1fr 1fr auto 90px 24px;
  align-items:center; gap:14px; width:100%;
  background:transparent; border:none; cursor:pointer;
  padding:14px 18px; text-align:left; transition:.12s;
  &:hover { background:${c.bg3}; }
  .nf { font-family:${c.mono}; font-size:14px; font-weight:700; color:${c.acc}; }
  .recipient { font-size:13px; color:${c.txt}; font-weight:500;
               white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; }
  .driver { font-size:11px; color:${c.txt2};
            white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; }
  .count {
    font-family:${c.mono}; font-size:13px; font-weight:700;
    color:${({ $complete }) => $complete ? c.acc : ({ $partial }) => $partial ? c.warn : c.txt3};
    text-align:center; min-width:60px;
  }
  .caret { color:${c.txt3}; font-size:11px;
           transform:${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0)'};
           transition:.2s; }

  /* Mobile/tablet: empilha em 3 linhas
     Linha 1: NF | StatusBadge | count | caret
     Linha 2: recipient (full)
     Linha 3: driver (full)
     A ordem dos children no JSX é:
       1=nf, 2=recipient, 3=driver, 4=StatusBadge, 5=count, 6=caret  */
  @media (max-width: 700px) {
    grid-template-columns: auto 1fr auto auto;
    gap: 4px 10px;
    padding: 12px 12px;

    > *:nth-child(1) { grid-row: 1; grid-column: 1; }          /* nf */
    > *:nth-child(4) { grid-row: 1; grid-column: 2; justify-self: end; }  /* StatusBadge */
    > *:nth-child(5) { grid-row: 1; grid-column: 3; }          /* count */
    > *:nth-child(6) { grid-row: 1; grid-column: 4; }          /* caret */
    > *:nth-child(2) { grid-row: 2; grid-column: 1 / -1; }     /* recipient */
    > *:nth-child(3) { grid-row: 3; grid-column: 1 / -1; }     /* driver */

    .nf       { font-size:13px; }
    .recipient{ font-size:12px; white-space:normal; line-height:1.3; }
    .driver   { font-size:10px; white-space:normal; line-height:1.3; }
    .count    { font-size:12px; min-width:auto; }
  }
`
export const AccordionStatusBadge = styled.span`
  font-size:10px; font-weight:600; padding:3px 10px; border-radius:11px;
  text-transform:uppercase; letter-spacing:.05em; white-space:nowrap;
  background:${({ $status }) =>
    $status === 'aguardando_recebimento' ? 'rgba(138,149,163,.15)' :
    $status === 'coletado_parcial'       ? 'rgba(245,158,11,.15)'  :
    $status === 'coletado'               ? 'rgba(59,130,246,.15)'  :
    $status === 'entregue'               ? 'rgba(0,184,122,.18)'   :
    $status === 'nao_embarcado'          ? 'rgba(239,68,68,.18)'   :
    'rgba(0,229,160,.15)'};
  border:1px solid ${({ $status }) =>
    $status === 'aguardando_recebimento' ? '#5a6370' :
    $status === 'coletado_parcial'       ? '#f59e0b' :
    $status === 'coletado'               ? '#3b82f6' :
    $status === 'entregue'               ? '#00b87a' :
    $status === 'nao_embarcado'          ? '#ef4444' :
    '#00b87a'};
  color:${({ $status }) =>
    $status === 'aguardando_recebimento' ? '#a8b3c1' :
    $status === 'coletado_parcial'       ? '#fbbf24' :
    $status === 'coletado'               ? '#60a5fa' :
    $status === 'entregue'               ? '#34d399' :
    $status === 'nao_embarcado'          ? '#fca5a5' :
    c.acc};
`
export const AccordionBody = styled.div`
  background:${c.bg3}; border-top:1px solid ${c.bdr2};
  padding:14px 18px; animation:${fadeInUp} .15s ease;
`
export const VolumeGrid = styled.div`
  display:grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap:8px;
`
export const VolumeChip = styled.div`
  display:flex; align-items:center; gap:10px;
  background:${({ $scanned, $embarked }) =>
    $embarked ? 'rgba(59,130,246,.06)' :
    $scanned  ? 'rgba(0,229,160,.06)'  :
    c.bg2};
  border:1px solid ${({ $scanned, $embarked }) =>
    $embarked ? c.info :
    $scanned  ? c.acc2 :
    c.bdr2};
  border-radius:8px; padding:8px 10px;
  .num {
    background:${({ $scanned, $embarked }) =>
      $embarked ? c.info :
      $scanned  ? c.acc  :
      c.bg3};
    color:${({ $scanned, $embarked }) =>
      $embarked || $scanned ? '#000' : c.txt3};
    border-radius:6px; padding:3px 8px;
    font-family:${c.mono}; font-size:11px; font-weight:700;
    min-width:34px; text-align:center;
  }
  .body { flex:1; min-width:0; }
  .rastreio { font-family:${c.mono}; font-size:11px; color:${c.txt};
              white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .box {
    font-size:10px; margin-top:2px;
    color:${({ $scanned, $embarked }) =>
      $embarked ? '#60a5fa' :
      $scanned  ? c.acc      :
      c.txt3};
    font-weight:${({ $scanned, $embarked }) => ($scanned || $embarked) ? '600' : '500'};
  }
`

// ─── BOXES tab (grid de cards) ─────────────────────────────────────────────
export const BoxesGrid = styled.div`
  display:grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap:12px;
  @media (max-width: 600px) { grid-template-columns: 1fr; gap: 10px; }
`
export const BoxCard = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:10px;
  padding:16px 18px; display:flex; flex-direction:column; gap:12px;
  transition:.15s; animation:${fadeInUp} .2s ease;
  cursor:pointer;
  &:hover { border-color:${c.acc2}; transform:translateY(-2px); box-shadow:0 8px 20px rgba(0,229,160,.08); }
  &:active { transform:translateY(-1px); }
  @media (max-width: 600px) { padding: 14px 14px; }
`

/* Modal de pedidos vinculados ao box (estilo aba Entregas, filtrado por box) */
export const BoxOrdersModalOverlay = styled.div`
  position:fixed; inset:0; z-index:2500;
  background:rgba(0,0,0,.7); backdrop-filter:blur(3px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center;
  padding:20px;
`
export const BoxOrdersModalBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:14px;
  width:1100px; max-width:calc(100vw - 24px);
  max-height:calc(100vh - 24px); display:flex; flex-direction:column;
  box-shadow:0 16px 48px rgba(0,0,0,.7);
  border-top:3px solid ${c.acc};
  animation:${fadeInUp} .15s ease;
`
export const BoxOrdersModalHeader = styled.div`
  padding:18px 22px 14px; border-bottom:1px solid ${c.bdr};
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  .info { min-width:0; }
  .name { font-family:${c.mono}; font-size:22px; font-weight:700; color:${c.acc}; }
  .meta { font-size:12px; color:${c.txt2}; margin-top:4px; }
  .close {
    background:none; border:none; color:${c.txt3}; cursor:pointer;
    width:32px; height:32px; border-radius:6px; font-size:20px;
    display:inline-flex; align-items:center; justify-content:center; flex-shrink:0;
    &:hover { background:${c.bg3}; color:${c.txt}; }
  }
`
export const BoxOrdersModalBody = styled.div`
  flex:1; overflow-y:auto; padding:14px 18px 18px;
  &::-webkit-scrollbar { width:5px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
`
export const BoxCardHeader = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  padding-bottom:10px; border-bottom:1px solid ${c.bdr};
  .name { font-family:${c.mono}; font-size:16px; font-weight:700; color:${c.acc}; }
  .count {
    font-family:${c.mono}; font-size:14px; font-weight:700;
    color:${({ $complete }) => $complete ? c.acc : c.warn};
  }
`
export const BoxStatsRow = styled.div`
  display:grid; grid-template-columns: repeat(3, 1fr); gap:8px;
`
export const BoxStatCell = styled.div`
  background:${c.bg3}; border:1px solid ${c.bdr}; border-radius:7px;
  padding:8px 10px;
  .val { font-family:${c.mono}; font-size:14px; font-weight:700;
         color:${({ $variant }) =>
           $variant === 'warn'   ? c.warn :
           $variant === 'accent' ? c.acc  :
           c.txt}; }
  .lbl { font-size:9px; color:${c.txt3}; text-transform:uppercase;
         letter-spacing:.06em; margin-top:2px; }
`
export const BoxDriversList = styled.div`
  display:flex; flex-direction:column; gap:6px;
  max-height:240px; overflow-y:auto; padding-right:4px;
  &::-webkit-scrollbar { width:4px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`
export const BoxDriverRow = styled.div`
  display:grid; grid-template-columns: 30px 1fr auto;
  align-items:center; gap:10px;
  background:${c.bg3}; border:1px solid ${c.bdr};
  border-left:3px solid ${({ $complete }) => $complete ? c.acc : c.warn};
  border-radius:6px; padding:7px 10px;
  .ini {
    width:28px; height:28px; border-radius:50%;
    background:${c.bg4}; border:1px solid ${c.bdr2};
    color:${c.txt2}; font-family:${c.mono}; font-size:11px; font-weight:700;
    display:flex; align-items:center; justify-content:center;
  }
  .name { font-size:12px; color:${c.txt}; font-weight:500;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .sub  { font-size:10px; color:${c.txt3}; margin-top:1px; }
  .count {
    font-family:${c.mono}; font-size:12px; font-weight:700;
    color:${({ $complete }) => $complete ? c.acc : c.warn};
    white-space:nowrap;
  }
`

// ─── CONFERÊNCIA · Tab box-first ───────────────────────────────────────────
export const ConfWrap = styled.div`
  display:flex; flex-direction:column; align-items:center; gap:14px;
  padding-top:8px;
`
export const ConfScanCard = styled.div`
  width:100%; max-width:620px;
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:14px;
  padding:24px 26px; text-align:center;
  .icon { font-size:42px; margin-bottom:8px; }
  h3 { font-size:16px; font-weight:600; color:${c.txt}; margin-bottom:4px; }
  p { font-size:12px; color:${c.txt3}; margin-bottom:18px; line-height:1.5; }
`
export const ConfScanInput = styled.input`
  width:100%; background:${c.bg3};
  border:2px solid ${({ $kind }) => $kind === 'err' ? c.danger : $kind === 'ok' ? c.acc : c.bdr2};
  border-radius:10px; padding:14px 16px;
  color:${c.txt}; font-size:16px; font-family:${c.mono};
  outline:none; text-align:center; letter-spacing:.04em;
  transition:.15s;
  &:focus { border-color:${c.acc}; box-shadow:0 0 0 4px rgba(0,229,160,.18); }
  &:disabled { opacity:.6; }
  &::placeholder { color:${c.txt3}; letter-spacing:.02em; font-size:13px; }
`
export const ConfFeedback = styled.div`
  margin-top:14px; min-height:18px;
  font-size:13px; font-weight:500;
  color:${({ $kind }) => $kind === 'ok' ? c.acc : $kind === 'err' ? c.danger : c.txt3};
`
export const ConfBoxBanner = styled.div`
  width:100%; max-width:620px;
  background:${c.bg2}; border:1px solid ${c.acc2}; border-radius:12px;
  padding:16px 20px;
  display:grid; grid-template-columns:1fr auto; gap:14px; align-items:center;
  animation:${fadeInUp} .2s ease;
  .info { min-width:0; }
  .lbl { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.06em; }
  .name { font-family:${c.mono}; font-size:22px; font-weight:700; color:${c.acc}; line-height:1.1; margin-top:2px; }
  .meta { font-size:11px; color:${c.txt2}; margin-top:4px; }
  .progress {
    font-family:${c.mono}; font-size:24px; font-weight:700;
    color:${({ $complete }) => $complete ? c.acc : c.warn};
    text-align:right; line-height:1;
  }
  .progress-lbl { font-size:9px; color:${c.txt3}; text-transform:uppercase;
                  letter-spacing:.06em; margin-top:2px; text-align:right; }
  @media (max-width: 600px) { padding:14px 16px; .name { font-size:18px; } .progress { font-size:20px; } }
`
export const ConfPanel = styled.div`
  width:100%; max-width:620px;
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:14px 18px;
  h4 { font-size:11px; font-weight:600; color:${c.txt3};
       text-transform:uppercase; letter-spacing:.06em; margin-bottom:10px; }
`
export const ConfVolumesList = styled.div`
  display:flex; flex-direction:column; gap:6px;
  max-height:380px; overflow-y:auto; padding-right:4px;
  &::-webkit-scrollbar { width:4px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`
export const ConfVolumeRow = styled.div`
  display:grid; grid-template-columns:28px 1fr auto; gap:10px;
  align-items:center;
  background:${({ $coletado, $embarked }) =>
    $embarked ? 'rgba(59,130,246,.06)' :
    $coletado ? 'rgba(0,229,160,.06)' : c.bg3};
  border:1px solid ${({ $coletado, $embarked }) =>
    $embarked ? c.info : $coletado ? c.acc2 : c.bdr};
  border-left:3px solid ${({ $coletado, $embarked }) =>
    $embarked ? c.info : $coletado ? c.acc : c.warn};
  border-radius:7px; padding:8px 12px;
  transition:.12s;
  ${({ $coletado }) => $coletado && `opacity:.85;`}
  .check { font-size:14px; text-align:center;
           color:${({ $coletado, $embarked }) =>
             $embarked ? c.info : $coletado ? c.acc : c.txt3}; }
  .body { min-width:0; }
  .rastreio { font-family:${c.mono}; font-size:12px; font-weight:700; color:${c.txt};
              white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .meta { font-size:10px; color:${c.txt3}; margin-top:2px;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .nf { font-family:${c.mono}; font-size:11px; color:${c.txt2}; font-weight:600; }
`
export const ConfResetBtn = styled.button`
  width:100%; max-width:620px;
  background:transparent; border:1px solid ${c.bdr2}; color:${c.txt3};
  padding:10px; border-radius:8px;
  font-size:12px; cursor:pointer; font-family:${c.sans};
  transition:.12s;
  &:hover { border-color:${c.danger}; color:${c.danger}; }
`

// ─── CONFERÊNCIA · Modal de alerta wrong-box (volume errado) ───────────────
export const WrongBoxModalOverlay = styled.div`
  position:fixed; inset:0; z-index:2000;
  background:rgba(0,0,0,.7); backdrop-filter:blur(3px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center;
  padding:20px;
`
export const WrongBoxModalBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.danger}; border-top:6px solid ${c.danger};
  border-radius:14px; padding:30px 32px;
  width:560px; max-width:calc(100vw - 20px);
  box-shadow:0 16px 48px rgba(239,68,68,.4);
  text-align:center;
  animation:${fadeInUp} .15s ease;
  .icon { font-size:64px; margin-bottom:10px; }
  h2 { font-size:20px; font-weight:700; color:${c.danger}; margin-bottom:10px; }
  .err-msg { font-size:14px; color:${c.txt}; line-height:1.5; margin-bottom:18px; }
  .correct-box {
    background:rgba(0,229,160,.10); border:1px solid ${c.acc2}; border-radius:10px;
    padding:14px 16px; margin-bottom:18px; text-align:left;
    .lbl { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.05em; }
    .name { font-family:${c.mono}; font-size:18px; font-weight:700; color:${c.acc}; margin-top:3px; }
    .bar { font-family:${c.mono}; font-size:11px; color:${c.txt3}; margin-top:3px; }
  }
  .order-info { font-size:11px; color:${c.txt2}; margin-top:6px; }
`
export const WrongBoxActions = styled.div`
  display:flex; gap:8px; justify-content:center;
  @media (max-width: 600px) { flex-direction:column-reverse; }
`
export const WrongBoxBtn = styled.button`
  padding:11px 22px; border-radius:8px; cursor:pointer;
  font-size:13px; font-weight:700; font-family:${c.sans};
  background:${({ $primary }) => $primary ? c.acc : 'transparent'};
  border:1px solid ${({ $primary }) => $primary ? c.acc : c.bdr2};
  color:${({ $primary }) => $primary ? '#000' : c.txt2};
  transition:.12s;
  &:hover { ${({ $primary }) => $primary ? 'filter:brightness(1.1);' : `border-color:${c.acc}; color:${c.acc};`} }
  @media (max-width: 600px) { width:100%; padding:14px; }
`
